import type {
  BindingId,
  ChannelAgentBinding,
  ChannelId,
  EntityId,
  TenantId,
} from '../core/contracts/channel-agent.js';

export class ChannelBindingRegistry {
  private bindings = new Map<string, ChannelAgentBinding>();

  bindAgent(binding: ChannelAgentBinding): void {
    if (this.bindings.has(binding.bindingId)) {
      throw new Error(`Binding ${binding.bindingId} already exists.`);
    }
    this.bindings.set(binding.bindingId, { ...binding });
  }

  getBinding(bindingId: BindingId): ChannelAgentBinding | undefined {
    const b = this.bindings.get(bindingId);
    return b ? { ...b } : undefined;
  }

  getChannelBindings(channelId: ChannelId, tenantId?: TenantId): ChannelAgentBinding[] {
    const list: ChannelAgentBinding[] = [];
    for (const b of this.bindings.values()) {
      if (b.channelId === channelId) {
        if (!tenantId || b.tenantId === tenantId) {
          list.push({ ...b });
        }
      }
    }
    return list;
  }

  updateBindingStatus(
    bindingId: BindingId,
    status: 'active' | 'paused' | 'revoked',
    updatedBy: EntityId
  ): ChannelAgentBinding {
    const b = this.bindings.get(bindingId);
    if (!b) throw new Error(`Binding ${bindingId} not found`);

    b.status = status;
    b.updatedBy = updatedBy;
    b.version += 1;
    b.updatedAt = Date.now();

    this.bindings.set(bindingId, b);
    return { ...b };
  }

  updatePolicy(
    bindingId: BindingId,
    updates: Partial<Pick<ChannelAgentBinding, 'triggerPolicy' | 'knowledgePolicy' | 'capabilityPolicy'>>,
    updatedBy: EntityId
  ): ChannelAgentBinding {
    const b = this.bindings.get(bindingId);
    if (!b) throw new Error(`Binding ${bindingId} not found`);

    if (updates.triggerPolicy) b.triggerPolicy = { ...updates.triggerPolicy };
    if (updates.knowledgePolicy) b.knowledgePolicy = { ...updates.knowledgePolicy };
    if (updates.capabilityPolicy) b.capabilityPolicy = { ...updates.capabilityPolicy };

    b.updatedBy = updatedBy;
    b.version += 1;
    b.updatedAt = Date.now();

    this.bindings.set(bindingId, b);
    return { ...b };
  }
}
